import {ReactNode} from 'react';

import styled from 'styled-components';

import {ActionStyle, ActionType, BodyText, BodyTypes} from 'components/design-guide';
import Action from 'components/design-guide/Action';
import PhoneNumberAction from 'shared/components/PhoneNumberAction';
import FormattedMessage from 'shared/IntlFormatMessage/components/FormattedMessage';
import {GLOBAL_PHONE_NUMBERS} from 'shared/IntlFormatMessage/translations/globalTranslations';
import {Spacing} from 'styles/Base';
import {getOnlineTermsOfServiceURL, getPrivacyPolicyURL, getWebsitePrivacyPolicyURL} from 'utils/links/appLinks';

const Padding = styled.div<{size: string}>`
  display: block;
  height: ${({size}) => size};
`;

const ListItem = styled.li`
  margin: ${Spacing.ZERO_5} 0;
`;

const UnorderedList = styled.ul`
  margin-top: -${Spacing.ZERO_5};
`;

export const contactPhoneTexting = (
  <PhoneNumberAction
    styleProps={{
      actionStyle: ActionStyle.INLINE_TEXT,
      children: <FormattedMessage message={GLOBAL_PHONE_NUMBERS.CONTACT_TEXT_NOTIFICATION_PHONE_DISPLAY} />
    }}
    name="amica-contact-phone-link"
    phoneNumber={GLOBAL_PHONE_NUMBERS.CONTACT_TEXT_NOTIFICATION_PHONE_INTL}
  />
);

const InlineTextLink = ({name, url, children}: {name: string; url: string; children: ReactNode}) => (
  <Action {...{name}} actionStyle={ActionStyle.INLINE_TEXT} actionType={ActionType.NEWTAB} typeProps={{href: url}}>
    {children}
  </Action>
);

export const formatHTML: Record<string, JSX.Element | ((children: ReactNode) => JSX.Element)> = {
  b: (children: ReactNode) => <b>{children}</b>,
  em: (children: ReactNode) => <em>{children}</em>,
  br: <br />,
  contactPhone: (
    <PhoneNumberAction
      styleProps={{
        actionStyle: ActionStyle.INLINE_TEXT,
        children: <FormattedMessage message={GLOBAL_PHONE_NUMBERS.CONTACT_PHONE_DISPLAY} values={{withCall: false}} />
      }}
      name="amica-contact-phone-link"
    />
  ),
  contactPhoneTexting,
  contactPhoneLife: (
    <PhoneNumberAction
      styleProps={{
        actionStyle: ActionStyle.INLINE_TEXT,
        children: <FormattedMessage message={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_PHONE_DISPLAY} />
      }}
      name="amica-contact-phone-life-link"
      phoneNumber={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_PHONE_INTL}
    />
  ),
  contactPhoneLifeCSR: (
    <PhoneNumberAction
      styleProps={{
        actionStyle: ActionStyle.INLINE_TEXT,
        children: <FormattedMessage message={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_CSR_PHONE_DISPLAY} />
      }}
      name="amica-contact-phone-life-csr-link"
      phoneNumber={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_CSR_PHONE_INTL}
    />
  ),
  contactPhoneLifeQuote: (
    <PhoneNumberAction
      styleProps={{
        actionStyle: ActionStyle.INLINE_TEXT,
        children: (
          <FormattedMessage
            message={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_QUOTE_PHONE_DISPLAY}
            values={{withCall: false}}
          />
        )
      }}
      name="amica-contact-phone-life-quote-link"
      phoneNumber={GLOBAL_PHONE_NUMBERS.CONTACT_LIFE_QUOTE_PHONE_INTL}
    />
  ),
  lifeGeneralRepHours: <>Monday - Friday, 8 a.m. - 7 p.m. ET</>,
  lifeClaimsRepHours: <>weekdays from 8:30 a.m. - 5:00 p.m. ET</>,
  claimsRepHours: <>24/7</>,
  claimsRepChatHoursWeekdays: <>Monday through Friday, 8 a.m. to midnight ET</>,
  claimsRepChatHoursWeekends: <>Saturday, 10 a.m. to 5:30 p.m. ET</>,
  generalRepHoursWeekdays: <>Monday - Friday, 6 a.m. - 10 p.m. ET</>,
  generalRepHoursWeekends: <>Saturday - Sunday, 7 a.m. - 8 p.m. ET</>,
  li: (children: ReactNode) => <ListItem key={Math.random()}>{children}</ListItem>,
  liNoMargin: (children: ReactNode) => <li key={Math.random()}>{children}</li>,
  p: (children: ReactNode) => <BodyText type={BodyTypes.BODYP}>{children}</BodyText>,
  paddingLarge: <Padding size={Spacing.FOUR} />,
  paddingMedium: <Padding size={Spacing.TWO} />,
  paddingMediumPlus: <Padding size={Spacing.TWO_5} />,
  paddingMicro: <Padding size={Spacing.ZERO_5} />,
  paddingMicroPlus: <Padding size={Spacing.ZERO_75} />,
  paddingRegular: <Padding size={Spacing.ONE_5} />,
  paddingSmall: <Padding size={Spacing.ONE} />,
  paddingSmallPlus: <Padding size={Spacing.ONE_25} />,
  paddingTiny: <Padding size={Spacing.ZERO_25} />,
  paddingXLarge: <Padding size={Spacing.EIGHT} />,
  privacyPolicy: (children: ReactNode) => (
    <InlineTextLink name="privacy-policy-link" url={getPrivacyPolicyURL()}>
      {children}
    </InlineTextLink>
  ),
  websitePrivacyPolicy: (children: ReactNode) => (
    <InlineTextLink name="website-privacy-policy-link" url={getWebsitePrivacyPolicyURL()}>
      {children}
    </InlineTextLink>
  ),
  onlineTermsOfService: (children: ReactNode) => (
    <InlineTextLink name="online-terms-of-service-link" url={getOnlineTermsOfServiceURL()}>
      {children}
    </InlineTextLink>
  ),
  ul: (children: ReactNode) => <UnorderedList>{children}</UnorderedList>,
  sup: (children: ReactNode) => <sup>{children}</sup>
};
